import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { Navigate } from 'react-router-dom';
import { FaMoneyBillWave, FaCalculator, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './fees.css';

// Function to get cookie value by name
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

// Helper functions for default values
const getCurrentMonth = () => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return months[new Date().getMonth()];
};

const getTodayDate = () => {
  return new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
};

const AddFees: React.FC = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  const [formData, setFormData] = useState({
    month: getCurrentMonth(),
    amount_paid: '',
    date_paid: getTodayDate(),
    enrolled_class: '',
    collected_by: '',
    student_id: '',
    fee_structure: '',
    discount: '0.00',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [classes, setClasses] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [feeStructures, setFeeStructures] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [existingBalance, setExistingBalance] = useState<number>(0);
  const [isPaymentComplete, setIsPaymentComplete] = useState<boolean>(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/users/user');
        // Auto-fill collected_by with current user's name
        setFormData(prev => ({
          ...prev,
          collected_by: `${response.data.user.first_name} ${response.data.user.last_name}`
        }));
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching user data:', error);
        dispatch(setAuth(false));
        setError('Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [dispatch]);

  // Fetch classes and fee structures on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [classesResponse, feeStructuresResponse] = await Promise.all([
          axios.get('/Branches/classes/'),
          axios.get('/fees/fee-structures/'),
        ]);
        setClasses(classesResponse.data.results || classesResponse.data);
        setFeeStructures(feeStructuresResponse.data);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError('Failed to load form data');
      }
    };

    fetchInitialData();
  }, []);

  // Debounce helper
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (formData.enrolled_class) {
        fetchStudentsByClass(formData.enrolled_class, studentSearchTerm);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [studentSearchTerm, formData.enrolled_class]);

  // Fetch students based on the selected class and optional search term
  const fetchStudentsByClass = async (classId: string, searchQuery: string = '') => {
    try {
      let url = `/Branches/students/?class_id=${classId}`;
      if (searchQuery) {
        url += `&name=${encodeURIComponent(searchQuery)}`;
      }
      // Request a reasonable page size, though server might cap it.
      // Search is the primary way to find specific records.
      url += `&page_size=100`;

      const response = await axios.get(url);
      setStudents(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
      // Don't show error on every keystroke if empty, simple logging is enough for search
      // setError('Failed to load students for this class'); 
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Handle class selection
    if (name === 'enrolled_class') {
      // When class changes, reset search and fetch default list
      setStudentSearchTerm('');
      // fetchStudentsByClass is triggered by the useEffect on enrolled_class change + empty search, 
      // BUT we need to set state first.
      // Actually, relying on useEffect [studentSearchTerm, formData.enrolled_class] might be tricky if we want immediate feedback.
      // Let's rely on the useEffect for the fetch to avoid double calling.

      setFormData(prevState => ({ ...prevState, [name]: value, student_id: '' }));
      return;
    }

    // Auto-populate amount_paid when fee structure is selected
    if (name === 'fee_structure' && value) {
      const selectedFee = feeStructures.find((fs) => fs.id === parseInt(value));
      if (selectedFee) {
        const discount = parseFloat(formData.discount) || 0;
        const totalAmount = parseFloat(selectedFee.amount) - discount;
        setFormData(prev => ({
          ...prev,
          fee_structure: value,
          amount_paid: totalAmount > 0 ? totalAmount.toFixed(2) : ''
        }));
      }
      if (error) setError(null);
      return;
    }

    // Auto-update amount_paid when discount changes (if fee structure is selected)
    if (name === 'discount' && formData.fee_structure) {
      const selectedFee = feeStructures.find((fs) => fs.id === parseInt(formData.fee_structure));
      if (selectedFee) {
        const discount = parseFloat(value) || 0;
        const totalAmount = parseFloat(selectedFee.amount) - discount;
        setFormData(prev => ({
          ...prev,
          discount: value,
          amount_paid: totalAmount > 0 ? totalAmount.toFixed(2) : ''
        }));
      }
      if (error) setError(null);
      return;
    }

    // For all other inputs, update normally
    setFormData({ ...formData, [name]: value });

    // Clear error when user starts typing
    if (error) setError(null);
  };

  // Check for existing fee payments when student, month, and fee structure are selected
  useEffect(() => {
    const checkExistingPayments = async () => {
      // Only proceed if we have student, month AND fee structure selected
      // If fee structure is not selected, we can't calculate a specific balance against a target amount.
      if (formData.student_id && formData.month && formData.fee_structure) {
        try {
          const response = await axios.get(
            `/fees/fees/?student=${formData.student_id}`
          );

          // Filter for the selected month AND selected fee structure
          // This ensures we are checking the balance for "Tuition" vs "Tuition", not mixing with "Transport"
          const relevantFees = response.data.results.filter(
            (fee: any) =>
              fee.month === formData.month &&
              fee.fee_structure === parseInt(formData.fee_structure)
          );

          if (relevantFees.length > 0) {
            // Calculate total paid for this specific fee structure in this month
            const totalPaid = relevantFees.reduce(
              (sum: number, fee: any) => sum + parseFloat(fee.amount_paid.toString().replace(/[^0-9.-]+/g, '')),
              0
            );

            // Get target amount from the SELECTED fee structure (from local state)
            const feeStructureId = parseInt(formData.fee_structure);
            const selectedFeeStructure = feeStructures.find(fs => fs.id === feeStructureId);

            if (selectedFeeStructure) {
              // Use the discount from the form data (editable) or from previous records?
              // Ideally, total target = Structure Amount - Discount.
              // If we are paying a balance, the discount should probably remain consistent.
              // Logic: Use current form discount.
              const discount = parseFloat(formData.discount) || 0;
              const expectedTotal = parseFloat(selectedFeeStructure.amount) - discount;
              const balance = expectedTotal - totalPaid;

              setExistingBalance(balance > 0 ? balance : 0);
              setIsPaymentComplete(balance <= 0.5); // Use small epsilon for float comparison safety

              if (balance > 0.5) {
                // Auto-fill the remaining balance
                setFormData(prev => ({
                  ...prev,
                  amount_paid: balance.toFixed(2)
                }));
                setError(null);
                setSuccessMessage(null);
              } else if (balance <= 0.5) {
                setError(`Ardaygan mar hore ayuu bixiyay lacagta ${selectedFeeStructure.fee_name} ee bisha (${formData.month}). Haraaga lama hayo.`);
                setFormData(prev => ({
                  ...prev,
                  amount_paid: '0.00'
                }));
              }
            }
          } else {
            // No previous payments for this specific structure/month
            // Balance is just the full amount (managed by handleInputChange default)
            setExistingBalance(0);
            setIsPaymentComplete(false);
          }
        } catch (error) {
          console.error('Error checking existing payments:', error);
        }
      } else {
        setExistingBalance(0);
        setIsPaymentComplete(false);
      }
    };

    checkExistingPayments();
  }, [formData.student_id, formData.month, formData.fee_structure, formData.discount, feeStructures]);

  // Calculate total amount based on fee structure and discount
  const calculateTotalAmount = () => {
    const feeStructure = feeStructures.find((fs) => fs.id === parseInt(formData.fee_structure));
    const discount = parseFloat(formData.discount) || 0;
    return feeStructure ? parseFloat(feeStructure.amount) - discount : 0;
  };

  // Calculate balance
  const calculateBalance = () => {
    const total = calculateTotalAmount();
    const paid = parseFloat(formData.amount_paid) || 0;
    return total - paid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Check if payment is already complete
    if (isPaymentComplete) {
      setError(`Ardaygan mar hore ayuu bixiyay lacagta bisha ${formData.month}. Ma samayn kartid lacag bixin labaad.`);
      return;
    }

    // Validation
    if (!formData.enrolled_class || !formData.student_id || !formData.fee_structure) {
      setError('Fadlan buuxi dhamaan meelaha loo baahan yahay');
      return;
    }

    const totalAmount = calculateTotalAmount();
    const amountPaid = parseFloat(formData.amount_paid);

    if (amountPaid > totalAmount) {
      setError('Lacagta la bixiyay kama badnaan karto wadarta guud');
      return;
    }

    // If there's an existing balance, check if new payment exceeds it
    if (existingBalance > 0 && amountPaid > existingBalance) {
      setError(`Lacagta la bixiyay (KSh ${amountPaid.toLocaleString()}) way ka badantahay haraaga (KSh ${existingBalance.toLocaleString()})`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await axios.post('/fees/fees/', {
        student: formData.student_id,
        class_associated: formData.enrolled_class,
        amount_paid: amountPaid,
        total_amount: totalAmount,
        date_paid: formData.date_paid,
        month: formData.month,
        collected_by: formData.collected_by,
        fee_structure: formData.fee_structure,
        discount: parseFloat(formData.discount),
      });

      setSuccessMessage('Diiwaanka lacagta si guul leh ayaa loo kordhiyay!');
      setTimeout(() => {
        setIsSubmitted(true);
      }, 1500);
    } catch (error: any) {
      console.error('Error adding fee:', error);
      if (error.response?.data?.error) {
        // Handle the specific duplicate payment error from backend
        setError(error.response.data.error);
      } else if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        setError(errorMsg || 'Cilad ayaa ku timid ku darista lacagta. Fadlan isku day markale.');
      } else {
        setError('Cilad ayaa ku timid ku darista lacagta. Fadlan hubi internetkaaga.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Redirect to fees page after successful submission
  if (isSubmitted) {
    return <Navigate to="/fees" />;
  }

  if (loading) {
    return (
      <div className="loading-state" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
        <p>Soo djinayaa...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      {auth ? (
        <div className="fees-container add-fees-container">
          {/* Header */}
          <div className="fees-header">
            <div className="header-content">
              <div className="header-title">
                <FaMoneyBillWave className="header-icon" />
                <div>
                  <h2>Diiwaangeli Lacag Cusub</h2>
                  <p className="header-subtitle">Diiwaangeli lacag bixin arday cusub</p>
                </div>
              </div>
            </div>
          </div>

          <div className="form-grid">
            {/* Form Section */}
            <div className="form-card">
              {error && (
                <div className="alert-error">
                  <FaTimesCircle />
                  <span>{error}</span>
                </div>
              )}

              {successMessage && (
                <div className="alert-success">
                  <FaCheckCircle />
                  <span>{successMessage}</span>
                </div>
              )}

              {/* Existing Balance Alert */}
              {existingBalance > 0 && formData.student_id && formData.month && (
                <div className="alert-info" style={{ background: '#dbeafe', borderColor: '#3b82f6', color: '#1e40af' }}>
                  <FaMoneyBillWave />
                  <div style={{ flex: 1 }}>
                    <strong>Haraaga Jira La Helay!</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                      Ardaygan wuxuu leeyahay haraaga lacageed oo ah <strong>KSh {existingBalance.toLocaleString()}</strong> bisha {formData.month}.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        amount_paid: existingBalance.toFixed(2)
                      }));
                    }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      borderRadius: '6px',
                      background: '#3b82f6',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Dhameystir Haraaga
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="modern-form">
                {/* Class and Student Selection */}
                <div className="form-section">
                  <h3 className="section-title">Macluumaadka Ardayga</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Fasalka <span className="required">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="enrolled_class"
                        value={formData.enrolled_class}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Dooro Fasalka</option>
                        {classes.map((cls) => (
                          <option key={cls.id} value={cls.id}>{cls.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Ardayga <span className="required">*</span>
                      </label>

                      {/* Search Input for Students */}
                      {formData.enrolled_class && (
                        <input
                          type="text"
                          className="form-input mb-2"
                          placeholder="Raadi magaca ardayga..."
                          value={studentSearchTerm}
                          onChange={(e) => setStudentSearchTerm(e.target.value)}
                          style={{ marginBottom: '8px', fontSize: '14px' }}
                        />
                      )}

                      <select
                        className="form-select"
                        name="student_id"
                        value={formData.student_id}
                        onChange={handleInputChange}
                        required
                        disabled={!formData.enrolled_class}
                        size={formData.enrolled_class && studentSearchTerm ? 5 : 1}
                      >
                        <option value="">Dooro Ardayga ({students.length} la helay)</option>
                        {students.map((student) => (
                          <option key={student.id} value={student.id}>{student.name}</option>
                        ))}
                      </select>
                      {!formData.enrolled_class && (
                        <small className="form-hint">Fadlan horta dooro fasalka</small>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="form-section">
                  <h3 className="section-title">Faahfaahinta Lacag Bixinta</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Bisha <span className="required">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="month"
                        value={formData.month}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Dooro Bisha</option>
                        {['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December']
                          .map(month => (
                            <option key={month} value={month}>{month}</option>
                          ))}
                      </select>
                      <small className="form-hint" style={{ color: '#10b981' }}>
                        Default: Bisha hadda ({getCurrentMonth()})
                      </small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Taariikhda La Bixiyay <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-input"
                        name="date_paid"
                        value={formData.date_paid}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split('T')[0]}
                        required
                      />
                      <small className="form-hint" style={{ color: '#10b981' }}>
                        Default: Maanta
                      </small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Qaab Dhismeedka Lacagta <span className="required">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="fee_structure"
                        value={formData.fee_structure}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Dooro Qaab Dhismeedka</option>
                        {feeStructures.map((feeStructure) => (
                          <option key={feeStructure.id} value={feeStructure.id}>
                            {feeStructure.fee_name} - KSh {parseFloat(feeStructure.amount).toLocaleString()}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Sicir Dhimis (KSh)</label>
                      <input
                        type="number"
                        className="form-input"
                        name="discount"
                        value={formData.discount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Lacagta La Bixiyay (KSh) <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        name="amount_paid"
                        value={formData.amount_paid}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        required
                      />
                      <small className="form-hint" style={{ color: '#10b981' }}>
                        Si toos ah u buuxsamay. Waa la bedeli karaa hadii ay qayb tahay.
                      </small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Waxaa Ururiyay</label>
                      <input
                        type="text"
                        className="form-input"
                        name="collected_by"
                        value={formData.collected_by}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting || isPaymentComplete}
                >
                  {submitting ? (
                    <>
                      <div className="spinner-small"></div>
                      Howsha way socotaa...
                    </>
                  ) : isPaymentComplete ? (
                    <>
                      <FaCheckCircle />
                      Lacag Bixinta Way Dhamaatay
                    </>
                  ) : (
                    <>
                      <FaCheckCircle />
                      Gudbi Diiwaanka Lacagta
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Summary Card */}
            <div className="summary-card">
              <div className="summary-header">
                <FaCalculator className="summary-icon" />
                <h3>Soo Koobida Lacag Bixinta</h3>
              </div>

              <div className="summary-content">
                <div className="summary-row">
                  <span className="summary-label">Qaab Dhismeedka:</span>
                  <span className="summary-value">
                    {formData.fee_structure ?
                      feeStructures.find(fs => fs.id === parseInt(formData.fee_structure))?.fee_name || '-'
                      : '-'
                    }
                  </span>
                </div>

                <div className="summary-row">
                  <span className="summary-label">Lacagta Asalka ah:</span>
                  <span className="summary-value">
                    KSh {formData.fee_structure ?
                      parseFloat(feeStructures.find(fs => fs.id === parseInt(formData.fee_structure))?.amount || '0').toLocaleString()
                      : '0'
                    }
                  </span>
                </div>

                <div className="summary-row discount">
                  <span className="summary-label">Sicir Dhimis:</span>
                  <span className="summary-value">
                    - KSh {parseFloat(formData.discount || '0').toLocaleString()}
                  </span>
                </div>

                <div className="summary-divider"></div>

                <div className="summary-row total">
                  <span className="summary-label">Wadarta Guud:</span>
                  <span className="summary-value">
                    KSh {calculateTotalAmount().toLocaleString()}
                  </span>
                </div>

                <div className="summary-row paid">
                  <span className="summary-label">Lacagta La Bixiyay:</span>
                  <span className="summary-value">
                    KSh {parseFloat(formData.amount_paid || '0').toLocaleString()}
                  </span>
                </div>

                <div className="summary-row balance">
                  <span className="summary-label">Haraaga:</span>
                  <span className={`summary-value ${calculateBalance() > 0 ? 'negative' : 'positive'}`}>
                    KSh {calculateBalance().toLocaleString()}
                  </span>
                </div>

                {calculateBalance() > 0 && (
                  <div className="summary-note warning">
                    <FaTimesCircle />
                    <span>Qeyb ayaa la bixiyay - Haraaga ayaa harsan</span>
                  </div>
                )}

                {calculateBalance() === 0 && formData.amount_paid && (
                  <div className="summary-note success">
                    <FaCheckCircle />
                    <span>Lacagta oo dhan way la bixiyay</span>
                  </div>
                )}

                {calculateBalance() < 0 && (
                  <div className="summary-note error">
                    <FaTimesCircle />
                    <span>Lacagta la bixiyay way ka badan tahay wadarta!</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="auth-error">
          <h4>Ma aadan xaqiijin</h4>
          <p>Fadlan soo gal si aad u hesho boggan</p>
        </div>
      )}
    </div>
  );
};

export default AddFees;
